import { useState, useEffect, memo } from 'react';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Skeleton from '@mui/material/Skeleton';
import Zoom from '@mui/material/Zoom';
import DownloadIcon from '@mui/icons-material/Download';
import ImageIcon from '@mui/icons-material/Image';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { ReactComponent as WhatsAppLogo } from '@Assets/whatsAppDigitalGlyphGreen.svg';
import { useReduxFileStorage } from '@Hooks/reduxFileStorageHook/useReduxFileStorage';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import UserInfoService from '@Helpers/URLHelpers/UserInfoService';
import FileService from '@Helpers/URLHelpers/FileService';
import ErrorHandler from '@Root/ErrorHandler';
import fileDownload from 'js-file-download';
import '@Partials/_ComStyles.scss';
import './Comment.scss';

function Comment(props) {
    const [comment, setComment] = useState({});
    const [commentUser, setCommentUser] = useState({});
    const [commentLoading, setCommentLoading] = useState(true);
    const [file, setFile] = useState();

    const colorList = [
        '#2784B9', '#40c47d', '#FDB16C', '#b06060', '#f77474', '#6080b0', '#9a60b0', '#6cb060',
        '#b0ae60', '#65412b', '#cc9963', '#e1bd83', '#60748b', '#744700', '#c8d579', '#5f8fba',
        '#3e6073', '#8e6dc1', '#473785', '#9158a9', '#7f3f7a', '#583e84', '#073763', '#20124d',
        '#3d124d', '#64113f', '#780448', '#003e23', '#108764', '#1a4c3d', '#c8d77f', '#11888b',
        '#1a95bd', '#0d6c7b', '#25b56e', '#006e3d'
    ];

    const userInfoService = new UserInfoService();
    const { activeUserId } = useReduxActiveUser();
    const { currentFiles, downloadFileFromUrl } = useReduxFileStorage();
    const fileService = new FileService();
    const errorHandler = new ErrorHandler();
    const cn = 'comment';

    useEffect(() => {
        renderData();
    }, [props]);

    const renderData = async () => {
        setCommentLoading(true);
        let commentObj = props.commentList.find((c) => c.id === props.userInteraction.linkId);
        setComment(commentObj);
        try {
            if (!!commentObj?.userId) {
                let user = await userInfoService.getUserById(commentObj?.userId);
                user = JSON.parse(user.body);
                setCommentUser(user);
            }
            if (commentObj?.type === 'File') {
                let fileObj = currentFiles[commentObj.fileId];
                if(!!fileObj) {
                    setFile(fileObj);
                }
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setCommentLoading(false);
    };

    const getInitials = (string) => {
        if (!!string && string.length > 0) {
            var names = string?.split(' '),
                initials = names[0]?.substring(0, 1)?.toUpperCase();
            if (names?.length > 1) initials += names[names?.length - 1]?.substring(0, 1)?.toUpperCase();
            return initials;
        }
        return 'N/A';
    };

    const dataURLtoFile = (dataurl) => {
        var arr = dataurl.split(','),
            mime = arr[0].match(/:(.*?);/)[1],
            bstr = atob(arr[1]),
            n = bstr.length,
            u8arr = new Uint8Array(n);
        while (n--) u8arr[n] = bstr.charCodeAt(n);
        return new File([u8arr], { type: mime });
    };

    const downloadFile = async() => {
        try {
            console.log(file);
//            downloadFileFromUrl(file?.publicUrl, file?.fileName);
        } catch (err) {
            errorHandler.handleError(err);
        }
    }

    const formatTime = (timestamp) => {
        return new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(timestamp);
    };

    const getUserName = () => {
        if (comment?.userType === 'reporterWhatsapp') return props?.ticket?.createdByFullName;
        const parts = commentUser?.fullName?.split(' ').filter(Boolean);
        if (!parts || parts.length === 0) return '';
        if (parts.length === 1) return parts[0];
        return parts[0] + ' ' + parts[parts.length - 1][0] + '.';
    };

    const getFileIcon = () => {
        if (file?.fileType?.includes('image')) return <ImageIcon />;
        if (file?.fileType?.includes('video')) return <VideoLibraryIcon />;
        if (file?.fileType?.includes('pdf')) return <PictureAsPdfIcon />;
        return <InsertDriveFileIcon />;
    };

    const renderAvatar = () => {
        const name = comment?.userType === 'reporterWhatsapp'
            ? props?.ticket?.createdByFullName
            : commentUser?.fullName;
        const bgColor = colorList[name?.charCodeAt(0) % colorList.length];
        const src = commentUser?.photo
            ? URL.createObjectURL(dataURLtoFile(commentUser?.photo))
            : undefined;

        return (
            <Avatar src={src} sx={{ width: 32, height: 32, bgcolor: bgColor, fontSize: '0.75rem', fontWeight: 600 }}>
                {getInitials(name)}
            </Avatar>
        );
    };

    const isSelf = activeUserId === commentUser?.id || JSON.stringify(activeUserId) == commentUser?.id;
    const isWhatsapp = comment?.userType === 'reporterWhatsapp';

    // WhatsApp only names the sender inside a bubble for messages that aren't yours — your own
    // sent messages never carry your own name back to you.
    const senderName = comment?.userType === 'reporterWhatsapp' ? props?.ticket?.createdByFullName : commentUser?.fullName;
    const senderColor = colorList[senderName?.charCodeAt(0) % colorList.length];

    const timeMeta = (
        <span className={cn + '__timeMeta'}>
            {isWhatsapp && <WhatsAppLogo className={cn + '__waIcon'} />}
            {formatTime(comment?.timeStamp)}
        </span>
    );

    if (commentLoading) {
        return (
            <Box className={cn + '__skeleton'}>
                <Skeleton variant="circular" width={32} height={32} />
                <Skeleton variant="rectangular" width="60%" height={44} sx={{ borderRadius: '7.5px' }} />
            </Box>
        );
    }

    return (
        <Box className={`${cn} ${isSelf ? cn + '--self' : ''}`}>
            {/* Avatar */}
            {!isSelf && <Box className={cn + '__avatar'}>{renderAvatar()}</Box>}

            {/* Bubble — WhatsApp keeps the sender name, message, and timestamp all inside the
                bubble itself rather than in a header row floating above it. */}
            <Box className={cn + '__bubble'}>
                {!isSelf && (
                    <Typography className={cn + '__senderName'} style={{ color: senderColor }}>
                        {getUserName()}
                    </Typography>
                )}

                {comment?.type === 'File' ? (
                    <Box className={cn + '__file'}>
                        {/* Image */}
                        {file?.fileType?.includes('image') && (
                            <Box className={cn + '__mediaPreview'}>
                                <img
                                    src={file?.publicUrl}
                                    className={cn + '__image'}
                                    alt={file?.fileName}
                                />
                            </Box>
                        )}
                        {/* Video */}
                        {file?.fileType?.includes('video') && file?.file && (
                            <Box className={cn + '__mediaPreview'}>
                                <video className={cn + '__video'} controls src={file?.publicUrl} />
                            </Box>
                        )}
                        {/* PDF */}
                        {file?.fileType?.includes('pdf') && (
                            <Box className={cn + '__mediaPreview'}>
                                <object className={cn + '__pdf'} data={file?.file} type="application/pdf" aria-label={file?.fileName} />
                            </Box>
                        )}
                        {/* File info bar */}
                        <Box className={cn + '__fileInfo'} onClick={downloadFile}>
                            <Box className={cn + '__fileIcon'}>{getFileIcon()}</Box>
                            <Tooltip title={file?.fileName} TransitionComponent={Zoom} arrow>
                                <Typography className={cn + '__fileName'}>{file?.fileName}</Typography>
                            </Tooltip>
                            <Tooltip title="Download" TransitionComponent={Zoom} arrow>
                                <IconButton size="small" className={cn + '__downloadBtn'}>
                                    <DownloadIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Box className={cn + '__fileMeta'}>{timeMeta}</Box>
                    </Box>
                ) : (
                    // The trailing time sits as a floated inline element straight after the
                    // text (real WhatsApp's own technique) so the last line of a wrapped
                    // message flows around it instead of it sitting on an awkward line alone.
                    <Typography className={cn + '__text'} component="p">
                        {comment?.text}
                        {timeMeta}
                    </Typography>
                )}
            </Box>

            {/* Avatar (self — right side) */}
            {isSelf && <Box className={cn + '__avatar'}>{renderAvatar()}</Box>}
        </Box>
    );
}

export default memo(Comment, arePropsEqual);

function arePropsEqual(oldProps, newProps) {
    return (
        JSON.stringify(oldProps?.commentList) === JSON.stringify(newProps?.commentList) &&
        JSON.stringify(oldProps.comment) === JSON.stringify(newProps.comment) &&
        oldProps.userInteraction.linkId === newProps.userInteraction.linkId &&
        oldProps?.index === newProps?.index
    );
}
